import { useState, useEffect, useCallback } from 'react';
import { _getTeamBalance, _getAnalyticsV2 } from 'controllers/lead-assignments/lead-assignments-controller';

export const useTeamBalance = () => {
  const [teamBalance, setTeamBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTeamBalance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await _getTeamBalance();
      const data = await response.json();
      setTeamBalance(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamBalance();
  }, [fetchTeamBalance]);

  return {
    teamBalance,
    loading,
    error,
    fetchTeamBalance,
  };
};

export const useTeamAnalytics = ({ from, to }) => {
  const [analytics, setAnalytics] = useState(null);
  const [previousAnalytics, setPreviousAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getPreviousRange = (from, to) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffDays = Math.round((toDate - fromDate) / (1000 * 60 * 60 * 24));
    const prevTo = new Date(fromDate);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - diffDays);
    return {
      from: `${prevFrom.toISOString().slice(0, 10)} 00:00`,
      to: `${prevTo.toISOString().slice(0, 10)} 23:59`,
    };
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const fetchAnalytics = useCallback(async ({ from, to }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await _getAnalyticsV2({ from, to });
      const data = await response.json();
      data.date = `${formatDate(from)} - ${formatDate(to)}`;
      setAnalytics(data);

      const { from: prevFrom, to: prevTo } = getPreviousRange(from, to);
      const prevResponse = await _getAnalyticsV2({ from: prevFrom, to: prevTo });
      const prevData = await prevResponse.json();
      prevData.date = `${formatDate(prevFrom)} - ${formatDate(prevTo)}`;
      setPreviousAnalytics(prevData);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics({ from, to });
  }, [fetchAnalytics, from, to]);

  return {
    analytics,
    previousAnalytics,
    loading,
    error,
    fetchAnalytics,
  };
};
